import React, { useState } from 'react';
import { HotelStaffMember } from '../../types/admin';

// Role configuration for the form with detailed permissions
const ROLE_OPTIONS: Record<string, { label: string; description: string; permissions: string[] }> = {
  hotel_admin: {
    label: 'Administrateur',
    description: 'Gestion complète de l\'établissement',
    permissions: ['Utilisateurs & rôles', 'Paramètres hôtel', 'Check-in / Check-out', 'Rapports & statistiques', 'Finances & factures', 'Messages', 'Alertes'],
  },
  hotel_manager: {
    label: 'Manager',
    description: 'Supervision des opérations hôtelières',
    permissions: ['Rapports & statistiques', 'Check-in / Check-out', 'Gestion du personnel', 'Messages', 'Alertes'],
  },
  hotel_supervisor: {
    label: 'Superviseur',
    description: 'Supervision de la réception et approbations',
    permissions: ['Check-in / Check-out', 'Validation des fiches', 'Messages', 'Alertes', 'Consultation clients'],
  },
  hotel_accountant: {
    label: 'Comptable',
    description: 'Finances, factures et rapports financiers',
    permissions: ['Rapports & statistiques', 'Finances & factures', 'Consultation clients', 'Alertes'],
  },
  receptionist: {
    label: 'Réceptionniste',
    description: 'Enregistrement des clients et gestion des séjours',
    permissions: ['Check-in / Check-out', 'Consultation clients', 'Messages', 'Alertes'],
  },
};

function generatePassword(): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const all = upper + lower + digits;
  const arr = new Uint8Array(12);
  crypto.getRandomValues(arr);
  let pw = '';
  pw += upper[arr[0] % upper.length];
  pw += lower[arr[1] % lower.length];
  pw += digits[arr[2] % digits.length];
  for (let i = 3; i < 12; i++) {
    pw += all[arr[i] % all.length];
  }
  return pw;
}

export interface StaffFormData {
  fullName: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  password?: string;
}

interface StaffFormProps {
  staff?: HotelStaffMember;
  onSave: (staffData: StaffFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function StaffForm({ staff, onSave, onCancel, isLoading = false }: StaffFormProps) {
  const isEdit = !!staff;
  const [formData, setFormData] = useState({
    fullName: staff?.fullName || '',
    email: staff?.email || '',
    phone: staff?.phone || '',
    role: staff?.role || 'receptionist',
    status: staff?.status || 'active' as string,
    password: isEdit ? '' : generatePassword(),
  });

  const [showPassword, setShowPassword] = useState(!isEdit);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Le nom complet est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    }

    if (!isEdit && !formData.password.trim()) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (!isEdit && formData.password.length < 8) {
      newErrors.password = 'Minimum 8 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const data: StaffFormData = { ...formData };
      if (isEdit && !data.password) delete data.password;
      onSave(data);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Modifier le Personnel' : 'Ajouter un Personnel'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Nom complet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom Complet *
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleChange('fullName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.fullName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Jean Dupont"
            />
            {errors.fullName && (
              <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              disabled={isEdit}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              } ${isEdit ? 'bg-gray-50 text-gray-500' : ''}`}
              placeholder="jean.dupont@hotel.com"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Téléphone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone *
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="+243 123 456 789"
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Mot de passe */}
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-24 font-mono text-sm ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <div className="absolute right-1 top-1 flex gap-1">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? 'Masquer' : 'Afficher'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange('password', generatePassword())}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  >
                    Generer
                  </button>
                </div>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Minimum 8 caractères, majuscule et chiffre requis. Communiquez ce mot de passe au membre du personnel.
              </p>
            </div>
          )}

          {/* Rôle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rôle
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(ROLE_OPTIONS).map(([key, role]) => (
                <option key={key} value={key}>
                  {role.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {ROLE_OPTIONS[formData.role]?.description || ''}
            </p>
            {ROLE_OPTIONS[formData.role]?.permissions && (
              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs font-medium text-gray-600 mb-1">Permissions :</p>
                <div className="flex flex-wrap gap-1">
                  {ROLE_OPTIONS[formData.role].permissions.map((perm) => (
                    <span key={perm} className="inline-block bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded">
                      {perm}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Statut */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Enregistrement...' : (isEdit ? 'Modifier' : 'Ajouter')}
          </button>
        </div>
      </div>
    </div>
  );
}
